import { ref, Ref, reactive, watch, computed } from 'vue';
import { OpenAI } from 'openai';
import type { Message } from '../types/chat';
import { meilisearchTools } from '../tools';
import { logAPICall, logToolCall } from '../logging/console';
import { logConversation } from '../logging/server';
import { trackError, trackHallucination } from '../logging/events';
import { filterForChatAPI } from '../utils/messageFilters';
import { createMessage, defaults } from '../messages';

const CHECK_HALLUCINATION_EVERY_N_CHARS = 200;
const MAX_HALLUCINATION_RETRIES = 2;
const FORBIDDEN_MESSAGE_SUBSTRINGS = [
    ...meilisearchTools.map(tool => tool.function.name),
    '_meiliSearchInIndex',
    '"index_uid"',
];

export function useChat() {
    const isStreaming = ref(false);
    const isFailure = ref(false);
    const messages: Ref<Message[]> = ref([
        createMessage('assistant', defaults.greeting),
    ]);

    const validUrls = computed(() => {
        const urls = new Set<string>();

        for (const message of messages.value) {
            for (const toolCall of message.tool_calls) {
                if (toolCall.function.name === '_meiliSearchSources') {
                    try {
                        const args = JSON.parse(toolCall.function.arguments);
                        if (args.sources && Array.isArray(args.sources)) {
                            for (const source of args.sources) {
                                if (source.url) {
                                    urls.add(source.url);
                                }
                            }
                        }
                    } catch (error) {
                        // Ignore JSON parse errors
                    }
                }
            }
        }

        return urls;
    });

    watch(
        () => JSON.stringify(messages.value.map(m => m.feedback).filter(f => f !== undefined)),
        () => logConversation(messages.value)
    );

    const { url, apiKeyChat } = window.MeiliSearchConfig;
    const { workspace, model, temperature } = window.chatConfig;
    const client = new OpenAI({
        baseURL: `${url}/chats/${workspace}`,
        apiKey: apiKeyChat,
        dangerouslyAllowBrowser: true,
    });

    const streamAssistantResponse = async () => {
        isStreaming.value = true;
        const apiMessages = filterForChatAPI(messages.value);

        logConversation(messages.value);
        logAPICall(apiMessages);

        const message = reactive<Message>(createMessage('assistant', '', true));
        const abortController = new AbortController();

        const abortStream = () => {
            abortController.abort('hallucination');
            message.isStreaming = false;
        }

        const handleHallucinatedLinks = (hallucinatedUrls: string[]) => {
            const remediationCount = messages.value.filter(
                m => m.internal && m.role === 'assistant' && m.content === defaults.remediation
            ).length;

            abortStream();
            trackHallucination(remediationCount + 1);
            console.warn(`⚠️ ${hallucinatedUrls.length} hallucinated link(s) detected:`, hallucinatedUrls);

            if (remediationCount >= MAX_HALLUCINATION_RETRIES) {
                if (message.content) message.contentBeforeAbort = message.content;
                message.content = defaults.failure;
                isFailure.value = true;
                console.error('❌ Too many hallucination retries, giving up.');
                trackError();
                return;
            }

            if (message.content) message.contentBeforeAbort = message.content;
            message.content = defaults.hallucination;

            const remediationMessage: Message = {
                role: 'assistant',
                content: defaults.remediation,
                timestamp: new Date(),
                isStreaming: false,
                tool_calls: [],
                internal: true,
            };
            messages.value.push(remediationMessage);

            // Repeat the user's last message to ensure conversation ends with user role
            const lastUserMessage = messages.value.findLast(m => m.role === 'user' && !m.internal);
            if (lastUserMessage) {
                const repeatedUserMessage: Message = {
                    role: 'user',
                    content: lastUserMessage.content,
                    timestamp: new Date(),
                    isStreaming: false,
                    tool_calls: [],
                    internal: true,
                };
                messages.value.push(repeatedUserMessage);
            }

            streamAssistantResponse();
        };

        const handleForbiddenContent = () => {
            abortStream();
            if (message.content) message.contentBeforeAbort = message.content;
            message.content = defaults.failure;
            isFailure.value = true;
            console.error('❌ Forbidden content detected in assistant response.');
            trackError();
        }

        /**
         * Catch hallucinations and other known issues in the incoming message.
         * @param isComplete Whether the message is done streaming.
         */
        const detectContentProblems = (isComplete = false) => {
            /*
             * Find all URLs in the message content using a regex that looks for
             * typical URL patterns. When the message is complete, include the
             * end of string terminator. Otherwise, this would flag partial URLs
             * while the message is still streaming.
             */
            const urlRegex = isComplete
                ? /(?<=\s|^|\]\()https?:\/\/[^\s)]+(?=\s|\)|$)/g
                : /(?<=\s|^|\]\()https?:\/\/[^\s)]+(?=\s|\))/g;
            const allUrls = Array.from(message.content.matchAll(urlRegex)).map(m => m[0]);
            const hallucinatedLinks = allUrls
                .filter(url => !validUrls.value.has(url))
                // Only check internal domains to allow external links from predefined responses
                .filter(url => url.startsWith(window.location.origin));

            if (hallucinatedLinks.length > 0) {
                handleHallucinatedLinks(hallucinatedLinks);
                return true;
            }

            if (FORBIDDEN_MESSAGE_SUBSTRINGS.some(substring => message.content.includes(substring))) {
                handleForbiddenContent();
                return true;
            }

            return false;
        };

        try {
            const stream = await client.chat.completions.create({
                model,
                temperature,
                messages: apiMessages,
                stream: true,
                tools: meilisearchTools,
                tool_choice: 'auto',
                // Parallel calls make OpenAI models leak multi_tool_use.parallel JSON into the content
                parallel_tool_calls: false,
            }, {
                signal: abortController.signal,
            });

            messages.value.push(message);

            let charsSinceLastCheck = 0;
            for await (const chunk of stream) {
                const delta = chunk.choices[0].delta;

                if (delta?.content) {
                    message.content += delta.content;
                    charsSinceLastCheck += delta.content.length;

                    if (charsSinceLastCheck >= CHECK_HALLUCINATION_EVERY_N_CHARS) {
                        charsSinceLastCheck = 0;
                        if (detectContentProblems(false)) {
                            break;
                        }
                    }
                }

                const tool_calls = chunk.choices[0]?.delta?.tool_calls;
                if (tool_calls) {
                    for (const tool_call of tool_calls) {
                        if (
                            !tool_call.function?.name ||
                            !['_meiliSearchProgress', '_meiliSearchSources', '_meiliAppendConversationMessage'].includes(
                                tool_call.function?.name,
                            )
                        ) {
                            console.warn('Unknown tool call:', tool_call);
                            continue;
                        }

                        const function_name = tool_call.function.name;
                        if (!tool_call.function.arguments) {
                            console.warn('Tool call missing arguments:', tool_call);
                            continue;
                        };
                        const function_arguments = JSON.parse(tool_call.function.arguments);

                        logToolCall(function_name, function_arguments);

                        if (['_meiliSearchProgress', '_meiliSearchSources'].includes(function_name)) {
                            message.tool_calls.push({
                                id: tool_call.id,
                                type: tool_call.type,
                                function: {
                                    name: function_name,
                                    arguments: tool_call.function.arguments,
                                },
                            });
                        }

                        if (function_name === '_meiliAppendConversationMessage') {
                            const messageToAppend: Message = {
                                role: function_arguments.role || 'tool',
                                content: function_arguments.content || '',
                                timestamp: new Date(),
                                isStreaming: false,
                                tool_calls: function_arguments.tool_calls || [],
                                tool_call_id: function_arguments.tool_call_id,
                                internal: true,
                            };

                            messages.value.push(messageToAppend);
                        }
                    }
                }
            }

            if (!abortController.signal.aborted) {
                detectContentProblems(true);
            }

            message.isStreaming = false;
            logConversation(messages.value);
        } catch (error: any) {
            if (abortController.signal.reason === 'hallucination') {
                return;
            }

            console.error('❌ An error occured while streaming the assistant response:', error);
            message.error = error;
            if (message.content) message.contentBeforeAbort = message.content;
            message.content = defaults.error;
            logConversation(messages.value);
            trackError();
        } finally {
            isStreaming.value = false;
        }
    };

    const sendMessage = async (content: string) => {
        messages.value.push(createMessage('user', content));
        await streamAssistantResponse();
    };

    return {
        messages,
        isStreaming,
        isFailure,
        sendMessage,
    };
}
